import { useEffect, useState } from "react";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import type { RuntimeWalletTransaction } from "../types/payments-wallets.types";

export interface UseStatementMovementResult {
  transaction: RuntimeWalletTransaction | null;
  /** The link names a movement this wallet cannot show: another wallet's, or one that does not exist. */
  notFound: boolean;
}

interface MovementRead {
  id: string;
  transaction: RuntimeWalletTransaction | null;
}

/**
 * The movement a `?movement=` link names. Free when it is on the loaded page; otherwise read by id, since a
 * link may point at any page and any filter.
 */
export function useStatementMovement(
  walletId: string,
  movementId: string | null,
  loaded: ReadonlyMap<string, RuntimeWalletTransaction>,
): UseStatementMovementResult {
  const onPage = movementId ? (loaded.get(movementId) ?? null) : null;
  const [read, setRead] = useState<MovementRead | null>(null);

  useEffect(() => {
    if (!movementId || onPage) return;
    let cancelled = false;
    PaymentsWalletsService.getTransaction(walletId, movementId)
      .then(({ data }) => {
        if (!cancelled) setRead({ id: movementId, transaction: data });
      })
      .catch(() => {
        if (!cancelled) setRead({ id: movementId, transaction: null });
      });
    return () => {
      cancelled = true;
    };
  }, [walletId, movementId, onPage]);

  if (!movementId) return { transaction: null, notFound: false };
  if (onPage) return { transaction: onPage, notFound: false };
  if (read?.id !== movementId) return { transaction: null, notFound: false };
  return { transaction: read.transaction, notFound: read.transaction === null };
}
